import { motion } from "framer-motion";
import { ShieldCheck, Award, Clock, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/constants";
import Seo from "../components/Seo";

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      <Seo
        title="About Hayat Security — 15+ Years of CCTV Installation in Nagpur"
        description="Founded by Murtaza Hasan, Hayat Security & Surveillance is Nagpur's most trusted CCTV installer with 15+ years of expertise, 10,000+ clients and 12,000+ completed projects across Sitabuldi, Dharampeth and all of Nagpur."
        canonicalPath="/about"
      />
      {/* Intro */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">About us</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-900 tracking-tight mt-3">
              15+ years making Nagpur safer, one camera at a time
            </h1>
            <p className="text-slate-600 mt-5 text-lg leading-relaxed">
              {BRAND.name} is a Nagpur-based security services company specialising in CCTV installation, networking, surveillance design and annual maintenance. Led by {BRAND.owner}, we've grown by focusing on one thing — doing every job well, every time.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.img
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            src="https://images.pexels.com/photos/11783119/pexels-photo-11783119.jpeg"
            alt="Surveillance room"
            className="rounded-3xl w-full h-[460px] object-cover border border-slate-200"
          />
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Our story</h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>
                What started as a small neighbourhood service in the bylanes of Central Avenue is today one of the most recommended CCTV and networking teams in Nagpur — trusted by homes, shops, showrooms, warehouses and offices alike.
              </p>
              <p>
                Our work is built on three simple ideas: recommend only hardware we'd use ourselves, install it like it's our own place, and stay reachable when something needs a second look. That's it. Everything else follows.
              </p>
              <p>
                Owner {BRAND.owner} still personally reviews large installations and AMC plans — because our name is on every panel we put up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">What drives us</h2>
            <p className="text-slate-600 mt-3">Four principles that shape every project we take on.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Award, title: "Experience", desc: "15+ years of hands-on field expertise across Nagpur." },
              { icon: ShieldCheck, title: "Reliability", desc: "Clean, labeled work. Equipment that lasts." },
              { icon: Clock, title: "Fast execution", desc: "Most installations completed in a single visit." },
              { icon: Users, title: "Strong after-service", desc: "AMC and repairs handled by the same team." },
            ].map((v, idx) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  className="bg-white rounded-2xl p-6 border border-slate-100 card-soft"
                  data-testid={`value-${v.title.toLowerCase()}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display font-semibold text-slate-900">{v.title}</div>
                  <div className="text-sm text-slate-600 mt-2 leading-relaxed">{v.desc}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Owner */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#1C6B8A] to-[#0f4a63] rounded-3xl p-8 md:p-14 text-white grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#8BD1E8]">Meet the owner</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Murtaza Hasan</h2>
              <p className="mt-4 text-white/85 leading-relaxed">
                A hands-on founder who still goes on-site for demanding installations. Murtaza built Hayat on a simple belief — security is personal, and the team setting it up should treat it that way.
              </p>
              <Link
                to="/contact"
                data-testid="about-contact-cta"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-[#1C6B8A] font-semibold hover:bg-slate-100"
              >
                Talk to our team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-2">
              <div className="grid grid-cols-3 gap-3 text-center">
                {[{ v: "15+", l: "Years" }, { v: "10K+", l: "Clients" }, { v: "12K+", l: "Projects" }].map((s) => (
                  <div key={s.l} className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/10">
                    <div className="font-display text-2xl font-bold">{s.v}</div>
                    <div className="text-xs text-white/80 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
